import { Autocomplete, TextField as MuiTextField, Chip, Box } from '@mui/material'
import { useState, useCallback, useEffect, useRef } from 'react'
import { apiGet } from '../../api/client'
import type { FieldMeta, ApiResponse } from '@crm/shared'

interface MultiLookupFieldProps {
  field: FieldMeta
  value: unknown
  mode: 'view' | 'edit'
  onChange?: (value: unknown) => void
  error?: string
  disabled?: boolean
  hideLabel?: boolean
}

interface LookupOption {
  id: string
  label: string
}

function getConfig(field: FieldMeta): { target_object_id: string; display_field: string } | null {
  if (!field.config) return null
  try {
    return JSON.parse(field.config as unknown as string)
  } catch {
    return null
  }
}

export function MultiLookupField({ field, value, mode, onChange, error, disabled, hideLabel }: MultiLookupFieldProps) {
  const config = getConfig(field)
  const [options, setOptions] = useState<LookupOption[]>([])
  const [inputValue, setInputValue] = useState('')
  const arr: string[] = Array.isArray(value) ? value.map(String) : value != null ? [String(value)] : []
  const [displayMap, setDisplayMap] = useState<Record<string, string>>({})
  const displayMapRef = useRef(displayMap)
  displayMapRef.current = displayMap
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const searchRecords = useCallback(async (keyword?: string) => {
    if (!config?.target_object_id) return
    try {
      const params = new URLSearchParams({ limit: '20' })
      if (keyword?.trim()) params.set('keyword', keyword.trim())
      const result = await apiGet<{ data: Array<{ id: string; data: Record<string, unknown> }> }>(
        `/objects/${config.target_object_id}/records?${params.toString()}`,
      )
      const displayField = config.display_field || 'name'
      const items = (result.data ?? []).map((r) => {
        const label = String(r.data[displayField] ?? r.id)
        return { id: r.id, label }
      })
      setOptions(items)
    } catch { /* ignore */ }
  }, [config])

  const debouncedSearch = useCallback((keyword: string) => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchRecords(keyword), 300)
  }, [searchRecords])

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  const loadDisplayValues = useCallback(async () => {
    if (!config?.target_object_id || arr.length === 0) return
    const currentMap = displayMapRef.current
    const idsToLoad = arr.filter((id) => !currentMap[id])
    if (idsToLoad.length === 0) return
    const displayField = config.display_field || 'name'
    const newMap: Record<string, string> = { ...currentMap }
    await Promise.all(idsToLoad.map(async (id) => {
      try {
        const result = await apiGet<ApiResponse<{ id: string; data: Record<string, unknown> }>>(
          `/objects/${config.target_object_id}/records/${id}`,
        )
        newMap[id] = String(result.data.data[displayField] ?? id)
      } catch {
        newMap[id] = id
      }
    }))
    setDisplayMap(newMap)
  }, [config, arr])

  useEffect(() => {
    if (mode === 'view' && arr.length > 0 && config?.target_object_id) {
      loadDisplayValues()
    }
  }, [mode, arr, config, loadDisplayValues])

  if (mode === 'view') {
    if (arr.length === 0) return <span />
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {arr.map((id) => (
          <Chip key={id} label={displayMap[id] || id} size="small" variant="outlined" />
        ))}
      </Box>
    )
  }

  const selected = arr.map((id) => ({ id, label: displayMap[id] || id }))

  return (
    <Autocomplete
      multiple
      size="small"
      disabled={disabled}
      options={options}
      getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, newInput) => {
        setInputValue(newInput)
        if (newInput.length >= 1) debouncedSearch(newInput)
      }}
      onChange={(_, newVal) => {
        onChange?.(newVal.map((v) => v.id))
        const map: Record<string, string> = {}
        newVal.forEach((v) => { map[v.id] = v.label })
        setDisplayMap(map)
      }}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={hideLabel ? undefined : field.label}
          error={!!error}
          helperText={error}
          required={!!field.is_required}
        />
      )}
    />
  )
}
